import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Cloud, Save, Link2, Unlink, FolderOpen, Copy, CheckCircle2, AlertTriangle } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const inputCls = "w-full h-9 rounded-md bg-zinc-900 border border-zinc-800 px-3 text-sm text-zinc-100 font-mono";

// Card Google Drive nella tab Backup: Client ID/Secret OAuth, collegamento account e stato upload notturno
export default function DriveBackupCard({ onChanged }) {
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_secret: "", folder_name: "", enabled: true });
  const [busy, setBusy] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const redirectUri = `${process.env.REACT_APP_BACKEND_URL}/api/drive/callback`;

  const load = async () => {
    try {
      const { data } = await api.get("/drive/config");
      setSt(data);
      setForm({ client_id: data.client_id || "", client_secret: "", folder_name: data.folder_name || "", enabled: data.enabled !== false });
      setShowSetup(!data.configured);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("drive") === "connected") toast.success("Google Drive collegato: i backup notturni verranno caricati automaticamente");
    if (p.get("drive") === "error") toast.error(`Collegamento Google Drive non riuscito (${p.get("msg") || "errore"})`);
    if (p.get("drive")) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/drive/config", { ...form, redirect_uri: redirectUri });
      setSt(data); setForm((f) => ({ ...f, client_secret: "" })); toast.success("Impostazioni Google Drive salvate"); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };
  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.get("/drive/connect", { params: { return_url: `${window.location.origin}${window.location.pathname}` } });
      window.location.href = data.authorization_url;
    } catch (err) { toast.error(formatApiError(err)); setBusy(null); }
  };
  const disconnect = async () => {
    if (!window.confirm("Scollegare Google Drive? I backup notturni non verranno più caricati.")) return;
    setBusy("disc");
    try { const { data } = await api.post("/drive/disconnect"); setSt(data); toast.success("Google Drive scollegato"); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };
  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post("/drive/test"); toast.success(`Cartella "${data.folder_name}" pronta su Drive`); await load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };
  const copyUri = () => { navigator.clipboard?.writeText(redirectUri); toast.success("URI copiato"); };

  if (!st) return null;
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3 space-y-2" data-testid="drive-backup-card">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-zinc-200 flex items-center gap-2"><Cloud className="h-4 w-4 text-emerald-300" /> Carica il backup notturno su Google Drive</div>
        <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="drive-enabled" className="h-4 w-4 accent-emerald-500" />
      </div>

      {st.connected ? (
        <div className="rounded-lg border border-emerald-800/50 bg-emerald-500/10 p-2 text-xs text-emerald-200 space-y-1" data-testid="drive-connected">
          <div className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Collegato {st.account_email ? <b data-testid="drive-account">{st.account_email}</b> : null} · dal {fmt(st.connected_at)}</div>
          {st.folder_link && <a href={st.folder_link} target="_blank" rel="noreferrer" className="underline flex items-center gap-1" data-testid="drive-folder-link"><FolderOpen className="h-3.5 w-3.5" /> Apri cartella "{st.folder_name}"</a>}
          {st.last_upload && <div data-testid="drive-last-upload">Ultimo caricamento: {st.last_upload.filename} · {fmt(st.last_upload.uploaded_at)}{st.last_upload.link && <> · <a href={st.last_upload.link} target="_blank" rel="noreferrer" className="underline">apri</a></>}</div>}
          {st.last_error && <div className="text-red-300 flex items-center gap-1" data-testid="drive-last-error"><AlertTriangle className="h-3.5 w-3.5" /> {st.last_error}</div>}
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" disabled={busy === "test"} onClick={test} data-testid="drive-test" className="border-zinc-800 h-8 text-zinc-200">Verifica</Button>
            <Button size="sm" variant="outline" disabled={busy === "disc"} onClick={disconnect} data-testid="drive-disconnect" className="border-zinc-800 h-8 text-red-300"><Unlink className="h-3.5 w-3.5 mr-1" /> Scollega</Button>
            <button type="button" onClick={() => setShowSetup((v) => !v)} data-testid="drive-toggle-setup" className="ml-auto text-[11px] text-zinc-400 underline">{showSetup ? "Nascondi credenziali" : "Credenziali OAuth"}</button>
          </div>
        </div>
      ) : (
        <div className="text-[11px] text-zinc-400" data-testid="drive-not-connected">
          {st.configured ? "Credenziali salvate: collega ora il tuo account Google per autorizzare il caricamento." : "Inserisci Client ID e Client Secret creati su Google Cloud Console (OAuth 2.0, tipo Web), poi collega l'account."}
        </div>
      )}

      {(showSetup || !st.connected) && (
        <div className="space-y-2" data-testid="drive-setup">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">URI di reindirizzamento autorizzato (da incollare in Google Cloud Console)</div>
            <div className="flex gap-2">
              <input readOnly value={redirectUri} className={inputCls} data-testid="drive-redirect-uri" />
              <Button size="sm" variant="outline" onClick={copyUri} data-testid="drive-copy-uri" className="border-zinc-800 h-9"><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="Client ID (…apps.googleusercontent.com)" className={inputCls} data-testid="drive-client-id" />
          <input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
            placeholder={st.has_secret ? `Client Secret salvato (${st.client_secret_masked}) — lascia vuoto per non cambiarlo` : "Client Secret (GOCSPX-…)"} className={inputCls} data-testid="drive-client-secret" />
          <input value={form.folder_name} onChange={(e) => setForm({ ...form, folder_name: e.target.value })} placeholder="Nome cartella su Drive" className={inputCls.replace(" font-mono", "")} data-testid="drive-folder-name" />
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" disabled={busy === "save"} onClick={save} data-testid="drive-save" className="h-9 bg-zinc-800 hover:bg-zinc-700"><Save className="h-3.5 w-3.5 mr-1" /> Salva</Button>
            {!st.connected && (
              <Button size="sm" disabled={!st.configured || busy === "connect"} onClick={connect} data-testid="drive-connect" className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white">
                <Link2 className="h-3.5 w-3.5 mr-1" /> {busy === "connect" ? "Reindirizzamento…" : "Collega Google Drive"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
